
import { useTaskOrchestration } from './useTaskOrchestration';
import { useState } from 'react';
import { useApprovalDialog } from "@/hooks/useApprovalDialog";
import { useRunMultiAgentTask } from './useRunMultiAgentTask';
import { ConversationContext } from './useA2AContextExtractors';
import { useScreeningAgent } from './useScreeningAgent';

/**
 * Top-level hook assembling logic for A2A orchestration.
 */
export function useA2AOrchestration() {
  const [conversationContext, setConversationContext] = useState<ConversationContext>({});
  const {
    isProcessing,
    orchestrationPlans,
    setIsProcessing,
    setOrchestrationPlans,
    updateOrchestrationPlans
  } = useTaskOrchestration();
  const { requestApproval, ApprovalDialogUI } = useApprovalDialog();

  // Integrate the screening agent capabilities
  const screeningAgent = useScreeningAgent();

  // State for agent/peer messages and responses (for completeness, but could also be lifted to context)
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);
  const [peerMessages, setPeerMessages] = useState<any[]>([]);
  const [agentResponses, setAgentResponses] = useState<Record<string, string>>({});

  const { runMultiAgentTask } = useRunMultiAgentTask({
    setIsProcessing,
    setOrchestrationPlans,
    setAgentResponses,
    setPeerMessages,
    setPendingMessages,
    requestApproval,
    updateOrchestrationPlans
  });

  /**
   * Handles screening-specific intent execution
   */
  const executeScreeningIntent = async (intent: string, params: any, sendMessage: any) => {
    const { candidate, jobTitle } = params;
    
    try {
      // Start with a phone screening
      const phoneScreenResult = await screeningAgent.conductLivePhoneScreen({
        candidateId: candidate.id,
        candidateName: `${candidate.first_name} ${candidate.last_name}`,
        candidatePhone: candidate.phone || '+15551234567', // Fallback for testing
        jobTitle: jobTitle || candidate.current_role || 'Software Developer'
      });
      
      if (!phoneScreenResult || screeningAgent.phoneScreening.status === 'failed') {
        sendMessage({
          id: Date.now().toString(),
          content: `I was unable to complete the phone screening for ${candidate.first_name}. There may be a connection issue or the candidate was unavailable.`,
          sender: 'screening-agent',
          timestamp: new Date()
        });
        return null;
      }
      
      // Send initial message with phone screening results
      sendMessage({
        id: Date.now().toString(),
        content: `I've completed the initial phone screening with ${candidate.first_name}. ${
          screeningAgent.phoneScreening.summary?.overallRecommendation === 'yes' || 
          screeningAgent.phoneScreening.summary?.overallRecommendation === 'strong_yes'
            ? 'The candidate shows promise and should proceed to the next stage.'
            : 'There are some concerns with this candidate based on the phone screening.'
        }`,
        sender: 'screening-agent',
        timestamp: new Date()
      });
      
      // If phone screening was successful, continue with deep interview
      if (phoneScreenResult && 
          (screeningAgent.phoneScreening.summary?.overallRecommendation === 'yes' || 
           screeningAgent.phoneScreening.summary?.overallRecommendation === 'strong_yes' ||
           screeningAgent.phoneScreening.summary?.overallRecommendation === 'maybe')) {
        
        const interviewResult = await screeningAgent.deepRoleInterview({
          candidateId: candidate.id,
          jobTitle: jobTitle || candidate.current_role || 'Software Developer',
          round: 2, // Complete assessment
          questionPayload: { 
            focus: 'technical' 
          }
        });
        
        if (interviewResult) {
          sendMessage({
            id: Date.now().toString() + '-interview',
            content: `I've completed a technical assessment for ${candidate.first_name}. ${
              screeningAgent.deepInterview.assessment?.overallAssessment || 
              "The candidate's technical skills align well with the role requirements."
            }`,
            sender: 'screening-agent',
            timestamp: new Date()
          });
        }
        
        // Finally run fraud detection
        const fraudResult = await screeningAgent.runFraudDetection({
          candidateId: candidate.id,
          transcript: screeningAgent.phoneScreening.transcript || "",
          resumeData: {
            education: [{ institution: "University", degree: "Bachelor's", dates: "2018-2022" }],
            experience: [{ 
              company: candidate.current_role || "Tech Company", 
              title: candidate.current_role || "Developer",
              dates: "2022-Present",
              description: "Worked on various projects"
            }],
            certifications: [],
            contactInfo: {
              email: candidate.email || "candidate@example.com",
              phone: candidate.phone || "+15551234567"
            }
          },
          candidateLocation: { ip: "192.168.1.1" }
        });
        
        if (fraudResult) {
          const fraudLevel = screeningAgent.fraudDetection.fraudScore || 0;
          
          sendMessage({
            id: Date.now().toString() + '-fraud',
            content: `I've completed the fraud detection analysis for ${candidate.first_name}. ${
              fraudLevel < 30 
                ? 'No fraud concerns were detected. The candidate appears legitimate.'
                : fraudLevel < 70
                ? 'Some potential inconsistencies were found. Manual verification is recommended.'
                : 'Significant fraud risk detected. Please review the candidate carefully before proceeding.'
            }`,
            sender: 'screening-agent',
            timestamp: new Date()
          });
        }
        
        // Final summary message
        sendMessage({
          id: Date.now().toString() + '-summary',
          content: `Screening assessment complete for ${candidate.first_name} ${candidate.last_name}. ${
            screeningAgent.phoneScreening.summary?.overallRecommendation === 'strong_yes' && 
            (screeningAgent.fraudDetection.fraudScore || 0) < 30
              ? 'This is a strong candidate who passed all screening checks. Recommended for next steps.'
              : screeningAgent.phoneScreening.summary?.overallRecommendation === 'yes' && 
                (screeningAgent.fraudDetection.fraudScore || 0) < 50
              ? 'This candidate meets the minimum requirements and passed basic screening. Consider for next round.'
              : 'This candidate requires additional review before proceeding.'
          }`,
          sender: 'screening-agent',
          timestamp: new Date()
        });
        
        return { 
          phoneScreen: phoneScreenResult, 
          interview: interviewResult, 
          fraudDetection: fraudResult 
        };
      }
      
      return { phoneScreen: phoneScreenResult };
      
    } catch (error) {
      console.error("Error in screening intent execution:", error);
      sendMessage({
        id: Date.now().toString() + '-error',
        content: `I encountered an error while screening ${candidate.first_name}: ${(error as Error).message || "Unknown error"}`,
        sender: 'screening-agent',
        timestamp: new Date()
      });
      return null;
    }
  };

  /**
   * Enhanced runMultiAgentTask that handles specific intents
   */
  const runEnhancedMultiAgentTask = async (
    task: string,
    capabilities: string[],
    complexity: 'simple' | 'medium' | 'complex' = 'medium',
    sendMessage: any
  ) => {
    // Check for specific screening intents
    if (task.toLowerCase().includes('screen candidate') || 
        capabilities.includes('conductLivePhoneScreen') ||
        capabilities.includes('screen-candidate')) {
      
      // Extract candidate info from context or task
      const candidateMatch = task.match(/screen candidate ([a-z\s]+)/i);
      const candidateName = candidateMatch ? candidateMatch[1] : 'John Doe';
      
      // For demo, we're using a mock candidate
      // In a real app, you'd query your database for the candidate
      const mockCandidate = {
        id: '123',
        first_name: candidateName.split(' ')[0],
        last_name: candidateName.split(' ').slice(1).join(' '),
        phone: '+15551234567',
        current_role: 'Software Developer'
      };
      
      // Extract job title
      const jobMatch = task.match(/position|role|job(?: of| for| as)? ([a-z\s]+)/i);
      const jobTitle = jobMatch ? jobMatch[1] : 'Software Developer';
      
      sendMessage({
        id: Date.now().toString(),
        content: `I'll initiate the screening process for ${candidateName} for the ${jobTitle} position.`,
        sender: 'executive-agent',
        timestamp: new Date()
      });
      
      // Execute the screening intent with extracted parameters
      return executeScreeningIntent('screen-candidate', { 
        candidate: mockCandidate, 
        jobTitle 
      }, sendMessage);
    }
    
    // Otherwise use the standard task execution
    return runMultiAgentTask(task, capabilities, complexity, sendMessage);
  };

  return {
    isProcessing,
    orchestrationPlans,
    setIsProcessing,
    setOrchestrationPlans,
    updateOrchestrationPlans,
    conversationContext,
    runMultiAgentTask: runEnhancedMultiAgentTask,
    ApprovalDialogUI,
    // The following are useful if needed in consuming UI:
    pendingMessages,
    agentResponses,
    peerMessages,
    setPendingMessages,
    setPeerMessages,
    setAgentResponses,
    // Expose screening agent capabilities directly
    screeningAgent
  };
}
